import React, { useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import api from '../services/api';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import { notificaErrore, notificaConferma, messaggioDa } from '../utils/notifiche';
import '../styles/DatiFiscali.css';

/**
 * DatiFiscali — i dati della societa' per la fattura elettronica e i codici
 * di pagamento (blocco 6, migration 258). Solo l'amministrazione.
 *
 * E' un modulo con il pulsante «Salva», non campi che si salvano da soli:
 * sono impostazioni fiscali (la regola di Alex del 24/09 le lascia al
 * pulsante), e un regime cambiato a meta' non deve finire in una fattura.
 * Le regole di ogni campo sono quelle del server (services/datiFiscali.js):
 * un valore che la fattura elettronica non accetterebbe non si salva, e il
 * messaggio dice perche'.
 */
const CODICI_MP = Array.from({ length: 23 }, (_, i) => `MP${String(i + 1).padStart(2, '0')}`);

const DatiFiscali = () => {
    const [campi, setCampi] = useState(null);
    const [valori, setValori] = useState({});
    const [metodi, setMetodi] = useState([]);
    const [inCorso, setInCorso] = useState(false);

    const carica = async () => {
        try {
            const r = await api.get('/dati-fiscali');
            setCampi(r.data.data.campi);
            const v = {};
            r.data.data.campi.forEach((c) => { v[c.chiave] = c.valore; });
            setValori(v);
            setMetodi(r.data.data.metodi);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a leggere i dati fiscali'));
        }
    };
    useEffect(() => { carica(); }, []);

    const salva = async () => {
        setInCorso(true);
        try {
            const r = await api.put('/dati-fiscali', valori);
            notificaConferma(r.data.message);
            await carica();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare i dati fiscali'));
        } finally {
            setInCorso(false);
        }
    };

    /* Un codice di pagamento e' un gesto solo: si salva alla scelta. */
    const salvaMetodo = async (codice, codiceMp) => {
        try {
            await api.put(`/dati-fiscali/metodi/${encodeURIComponent(codice)}`, { codice_mp: codiceMp });
            setMetodi((m) => m.map((x) => (x.codice === codice ? { ...x, codice_mp: codiceMp || null } : x)));
            notificaConferma('Codice salvato.');
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare il codice'));
        }
    };

    if (!campi) return null;
    return (
        <div className="dfi-pagina">
            <IntestazioneSezione titolo="Dati fiscali"
                icona={<Scale size={20} aria-hidden="true" />}
                sottotitolo="La società nella fattura elettronica, e il codice di ogni metodo di pagamento." />

            <section className="dfi-riquadro">
                <h3>La società</h3>
                <p className="dfi-aiuto">
                    Entrano in ogni fattura elettronica. Il regime fiscale e lo stato di liquidazione servono
                    sempre; il REA per una società iscritta; l&apos;IBAN solo per i bonifici.
                </p>
                <div className="dfi-campi">
                    {campi.map((c) => (
                        <label key={c.chiave} className="dfi-campo">
                            <span>{c.etichetta}</span>
                            <input value={valori[c.chiave] ?? ''}
                                   onChange={(e) => setValori((v) => ({ ...v, [c.chiave]: e.target.value }))} />
                        </label>
                    ))}
                </div>
                <button type="button" className="dfi-btn" onClick={salva} disabled={inCorso}>Salva</button>
            </section>

            <section className="dfi-riquadro">
                <h3>Metodi di pagamento</h3>
                <p className="dfi-aiuto">
                    Il codice che la fattura elettronica scrive per ogni incasso. Un metodo senza codice rende
                    «da correggere» la fattura che lo usa: il gestionale non ne sceglie uno a caso.
                    «Tracciato» è quello che il Sistema TS legge per il pagamento tracciato: stessa riga, una
                    fonte sola.
                </p>
                <div className="rm-scorre">
                    <table className="dfi-tabella">
                        <thead>
                            <tr><th>Metodo</th><th>Tracciato</th><th>Codice</th></tr>
                        </thead>
                        <tbody>
                            {metodi.map((m) => (
                                <tr key={m.codice}>
                                    <td>{m.descrizione}{!m.attivo && <em> (non attivo)</em>}</td>
                                    <td>{m.tracciato ? 'sì' : 'no'}</td>
                                    <td>
                                        <select value={m.codice_mp || ''} aria-label={`Codice di ${m.descrizione}`}
                                                onChange={(e) => salvaMetodo(m.codice, e.target.value)}>
                                            <option value="">— nessuno —</option>
                                            {CODICI_MP.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default DatiFiscali;
